import { type AreaUnits, area, convertArea, convertLength, feature, length, type Units } from '@turf/turf';
import { round } from 'lodash';
import type { UnitSystem } from '~/common/types';

interface AreaUnitProps {
  unitType?: AreaUnits;
  unitSystem: UnitSystem;
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | GeoJSON.LineString;
}

interface LengthUnitProps {
  unitType?: Units;
  unitSystem: UnitSystem;
  geometry: GeoJSON.Polygon | GeoJSON.LineString | GeoJSON.MultiLineString;
}

export const unitAbbreviation: Map<Units | AreaUnits, string> = new Map([
  ['inches', 'in'],
  ['feet', 'ft'],
  ['yards', 'yd'],
  ['miles', 'mi'],
  ['meters', 'm'],
  ['kilometers', 'km'],
  ['acres', 'ac'],
]);

/**
 * `convertArea` & `convertLength` each takes in three parameters: area (number), originalUnit (Units), finalUnit (Units). https://turfjs.org/docs/#convertArea
 *
 * @param geometry {GeoJSON.Polygon | GeoJSON.LineString} - the geometry to calculate the area of.
 * @param unitType {Units} - (optional) the unit type to convert to (aka finalUnit). "meters" | "millimeters" | "centimeters" | "kilometers" | "acres" | "miles" | "nauticalmiles" | "inches" | "yards" | "feet" | "radians" | "degrees" | "hectares".
 * @param unitSystem {UnitSystem} - the unit system to use. "metric" | "imperial".
 * @returns { value: number, unit: string}
 */
export const calculateArea = ({ unitType, unitSystem, geometry }: AreaUnitProps) => {
  let finalUnitType: string = 'acres';
  if (unitType !== undefined) {
    finalUnitType = unitType;
  } else if (unitSystem === 'metric') {
    unitType = 'meters';
  } else if (unitSystem === 'imperial') {
    unitType = 'acres';
  }

  const sup2 = '\u00B2';

  if (unitType !== 'acres') {
    finalUnitType = unitAbbreviation.get(unitType!) + sup2;
  } else {
    finalUnitType = 'acres';
  }

  return {
    value: round(convertArea(area(geometry), 'meters', unitType), 2),
    unit: finalUnitType,
  };
};

export const calculateLength = ({ unitType, unitSystem, geometry }: LengthUnitProps) => {
  if (unitType === undefined) {
    if (unitSystem === 'metric') {
      unitType = 'meters';
    } else if (unitSystem === 'imperial') {
      unitType = 'feet';
    }
  }

  const value = round(convertLength(length(feature(geometry)), 'kilometers', unitType), 2);

  const unitShorthand = unitAbbreviation.get(unitType!)!;

  return {
    value,
    unit: unitShorthand,
  };
};
